import React, { useMemo, useState } from "react";
import { Box, LinearProgress, Link, Paper, useTheme } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import { tokens } from "../../../assets/theme";
import { getAssetWorkOrderHistory } from "../../../fetchers/cmmsWorkOrder";
import { renderBadge } from "../../cmmsWorkOrders/components/WorkOrdersDataGrid";
import workOrderMessages from "../../cmmsWorkOrders/messages";
import { buildWorkOrderDeepLink } from "../../cmmsWorkOrders/workOrderDeepLink";
import { getStatusBadge, getTypeBadge } from "../../cmmsWorkOrders/workOrderEnums";
import { EMPTY_VALUE, formatTimestamp } from "../cmmsFormat";
import { getAssetHierarchyPalette } from "../assetHierarchyStyles";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton } from "./CmmsStates";
import messages from "../messages";

/*
 * The finished work on a node: its completed and closed work orders, and those of everything
 * below it, most recently finished first.
 *
 * Paged on the server. A site's history is every order its machines have ever had, which is
 * the one list in the asset panel that grows without bound, so the grid asks for one page at a
 * time and the footer's total comes from the response rather than from counting rows.
 *
 * Sorting and filtering are off for the same reason: on a server-paged grid they would reorder
 * only the rows on screen, which reads as sorting the history while quietly not doing it. The
 * order is the server's — most recently finished first — and that is the order history is read in.
 *
 * The title is a real link to the work order page, not a row click, so ctrl-click opens the
 * order in a new tab and the asset panel stays where it was.
 *
 * Only mounted while its tab is selected, and keyed by node in AssetNodeDetailsPanel, so paging
 * starts over on a new node and keepPreviousData can only ever hold on to this node's rows.
 */
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 25;

export default function AssetWorkOrderHistoryTab({ companyKey, assetNodeId }) {
  const theme = useTheme();
  const intl = useIntl();
  const colors = tokens(theme.palette.mode);
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const language = localStorage.getItem("language") || "en";
  const translations = language === "sv" ? svSE : enUS;

  // The grid counts pages from 0 and the API from 1; the conversion happens once, here.
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const page = paginationModel.page + 1;
  const { pageSize } = paginationModel;

  const historyQuery = useQuery({
    // The work order page's key prefix, so the invalidations that already exist — the CMMS
    // SignalR events, ReopenDialog, QuickReportDialog — refresh this tab too. `view` keeps the
    // filter object distinct from MachineWorkOrdersTab's plain { assetNodeId }, whose data is an
    // array rather than a page.
    queryKey: ["cmmsWorkOrders", companyKey, { assetNodeId, view: "history", page, pageSize }],
    queryFn: () => getAssetWorkOrderHistory(companyKey, assetNodeId, { page, pageSize }),
    staleTime: 30_000,
    // The current page stays on screen while the next one loads, rather than the grid
    // collapsing to a skeleton on every click of the pager.
    keepPreviousData: true,
    enabled: Boolean(companyKey && assetNodeId),
  });

  const accent = cmmsColors.accent;
  const columns = useMemo(
    () => [
      {
        field: "title",
        headerName: intl.formatMessage(workOrderMessages.fieldTitle),
        flex: 2,
        minWidth: 180,
        renderCell: (params) => (
          <Link
            component={RouterLink}
            to={buildWorkOrderDeepLink(params.row.id)}
            underline="hover"
            sx={{ color: accent, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {params.value}
          </Link>
        ),
      },
      // At a site most rows belong to nodes below it, so which one is worth a column.
      { field: "assetNodeName", headerName: intl.formatMessage(workOrderMessages.fieldAsset), flex: 1, minWidth: 140 },
      {
        field: "workOrderType",
        headerName: intl.formatMessage(workOrderMessages.fieldType),
        width: 130,
        renderCell: (params) => renderBadge(getTypeBadge(params.value, intl)),
      },
      {
        field: "status",
        headerName: intl.formatMessage(workOrderMessages.fieldStatus),
        width: 120,
        renderCell: (params) => renderBadge(getStatusBadge(params.value, intl)),
      },
      // The date the server sorts on, as the server works it out — when the work ended, else
      // when the order was closed, else its last update — rather than a second copy of that
      // rule here that could drift from it.
      {
        field: "finishedAt",
        headerName: intl.formatMessage(messages.historyColumnFinished),
        width: 170,
        valueFormatter: (params) => formatTimestamp(intl, params.value),
      },
      {
        field: "assignedResourceName",
        headerName: intl.formatMessage(workOrderMessages.fieldAssigned),
        flex: 1,
        minWidth: 140,
        valueFormatter: (params) => params.value ?? EMPTY_VALUE,
      },
    ].map((column) => ({ ...column, sortable: false, filterable: false })),
    [intl, accent],
  );

  // Stable across renders: a slot defined inline would remount the overlay on every render.
  // Inherit + accent because the theme's primary is the grid's own surface colour.
  const gridSlots = useMemo(
    () => ({ loadingOverlay: () => <LinearProgress color="inherit" sx={{ color: accent }} /> }),
    [accent],
  );

  if (historyQuery.isLoading) {
    return <CmmsListSkeleton rows={5} />;
  }

  if (historyQuery.isError) {
    return (
      <CmmsErrorState
        error={historyQuery.error}
        fallback={intl.formatMessage(messages.historyLoadFailed)}
        onRetry={historyQuery.refetch}
        isRetrying={historyQuery.isFetching}
      />
    );
  }

  const history = historyQuery.data;

  if (!history || history.totalCount === 0) {
    return (
      <CmmsEmptyState
        dense
        icon={<HistoryIcon />}
        title={intl.formatMessage(messages.historyEmptyTitle)}
        hint={intl.formatMessage(messages.historyEmptyHint)}
      />
    );
  }

  return (
    <Paper sx={{ borderRadius: "4px" }}>
      {/* Grows with the page instead of the fixed 70vh box the full-page grids sit in: this
          one shares a narrow panel with the node's details above it. */}
      <Box
        sx={{
          "& .MuiDataGrid-root": {
            backgroundColor: colors.primary[400],
            borderRadius: "0px",
            fontSize: "0.95rem",
            border: "0 !important",
          },
          "& .MuiDataGrid-cell": { borderColor: `${colors.primary[700]} !important` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.primary[600],
          },
        }}
      >
        <DataGridPro
          autoHeight
          rows={history.items}
          getRowId={(row) => row.id}
          columns={columns}
          pagination
          paginationMode="server"
          // When a reopened order shrinks the total under the current page, the grid moves
          // back to the last page itself and reports it through onPaginationModelChange.
          rowCount={history.totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={historyQuery.isPreviousData}
          slots={gridSlots}
          localeText={translations.components.MuiDataGrid.defaultProps.localeText}
          disableColumnMenu
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
